import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  Stack,
  LinearProgress,
  Autocomplete,
  TextField,
  Chip,
  Tooltip,
  Divider,
  Drawer,
  IconButton,
  Radio,
  Checkbox,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSettingsContext } from 'src/components/settings';
import { useParams, usePathname, useRouter, useSearchParams } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
import Chart, { useChart } from 'src/components/chart';
import axiosInstance, { postFetcher } from 'src/utils/axios';
import { useAuthContext } from 'src/auth/hooks';
import { formatMarks } from './ExaminationResultRow';

const formatTimeSpent = (secs) => {
  if (secs === null || secs === undefined || secs === '') return '-';
  const s = parseInt(secs, 10);
  if (isNaN(s) || s < 0) return '-';
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  if (h > 0) {
    return rem > 0 ? `${h}h ${m}m ${rem}s` : `${h}h ${m}m`;
  }
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
};

const columnALabels = ['a', 'b', 'c', 'd'];
const columnBLabels = ['p', 'q', 'r', 's'];

export default function ExaminationProfileView({
  propId,
  quizId: propQuizId,
  studentId: propStudentId,
  isEmbedded = false,
  quizzes: propQuizzes,
  isTalentHunt = false,
  reportData: suppliedReportData,
  previewPaper,
  backPath,
  practiceMode = false,
} = {}) {
  const theme = useTheme();
  const settings = useSettingsContext();
  const params = useParams();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user } = useAuthContext();

  const { id } = params;
  const effectiveResultId = isEmbedded ? propId : (propId || (id && id !== 'report' && id !== 'preview' ? id : null));
  const quizIdParam = searchParams.get('quiz_id');
  const effectiveQuizIdParam = propQuizId || quizIdParam;
  const studentIdParam = searchParams.get('student_id');

  const isTalentHuntParam = searchParams.get('is_talent_hunt') === 'true';
  const isHeightStudentDashboard =
    pathname?.includes('/heightstudentdashboard') || pathname?.includes('/height-student');
  const effectiveIsTalentHunt =
    isTalentHunt || isTalentHuntParam || isHeightStudentDashboard || Boolean(user?.is_height_student);
  const [reportData, setReportData] = useState(suppliedReportData || null);
  const effectiveStudentId =
    propStudentId ||
    studentIdParam ||
    reportData?.student_id ||
    reportData?.student?.id ||
    reportData?.student_info?.id ||
    (user?.is_student ? (user?.student_id || user?.id) : null) ||
    (user?.is_height_student ? (user?.id || user?.height_student_id) : null);
  const targetStudentId = effectiveStudentId;
  const initialReportType = practiceMode ? 'test_analysis' : searchParams.get('report_type') || 'test_analysis';

  // Support 2 unified tabs: 'test_analysis' and 'report_card'
  const [activeTab, setActiveTab] = useState(
    initialReportType === 'report_card' ? 'report_card' : 'test_analysis'
  );

  useEffect(() => {
    if (practiceMode) return;
    const reportTypeParam = searchParams.get('report_type');
    if (reportTypeParam) {
      setActiveTab(reportTypeParam === 'report_card' ? 'report_card' : 'test_analysis');
    }
  }, [searchParams, practiceMode]);

  const isParent = pathname?.includes('/parentdashboard') || pathname?.includes('/parent-dashboard');
  const isHeightStudent =
    isHeightStudentDashboard ||
    pathname?.includes('/heightstudent') ||
    Boolean(user?.is_height_student) ||
    Boolean(effectiveIsTalentHunt) ||
    Boolean(isEmbedded);
  const isStudent =
    !isHeightStudent &&
    (pathname?.includes('/studentdashboard') || pathname?.includes('/student-dashboard'));
  const isParentOrStudentDashboard = isParent || isStudent || isHeightStudent;
  const shouldGateByAnswerKey = isParentOrStudentDashboard;
  const canShowDetailedResponses = shouldGateByAnswerKey
    ? Boolean(reportData?.show_answer_key)
    : true;
  const canShowExamPreview = shouldGateByAnswerKey
    ? Boolean(reportData?.show_answer_key ?? reportData?.quiz_show_answer_key)
    : true;

  const resolvedDashboardRoot = isParent
    ? paths.parentdashboard.root
    : isHeightStudent
    ? paths.heightstudentdashboard.root
    : isStudent
    ? paths.studentdashboard.root
    : paths.dashboard.root;

  const resolvedResultList =
    backPath ||
    (isParent
      ? paths.parentdashboard.testresults
      : isHeightStudent
      ? paths.heightstudentdashboard.examinationresult.root
      : isStudent
      ? paths.studentdashboard.examinationresult.root
      : paths.dashboard.examinationresult.root);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Quiz list and dropdown state
  const [quizList, setQuizList] = useState(
    Array.isArray(propQuizzes)
      ? Array.from(new Map(propQuizzes.map((q) => [q.quiz_id || q.id, q])).values())
      : []
  );
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);

  const currentEffectiveQuizId = selectedQuiz?.quiz_id || selectedQuiz?.id || effectiveQuizIdParam || reportData?.quiz_id;

  // Exam Preview Drawer states
  const [examPreviewOpen, setExamPreviewOpen] = useState(false);
  const [examPreviewQuizData, setExamPreviewQuizData] = useState(null);
  const [loadingExamPreview, setLoadingExamPreview] = useState(false);
  const [examPreviewError, setExamPreviewError] = useState(null);
  const [examPreviewSectionTab, setExamPreviewSectionTab] = useState('all');
  const [examPreviewNavSectionTab, setExamPreviewNavSectionTab] = useState('all');
  const [examPreviewMobileNavOpen, setExamPreviewMobileNavOpen] = useState(false);

  const handleOpenExamPreview = async () => {
    if (!currentEffectiveQuizId) return;
    setExamPreviewOpen(true);
    setExamPreviewError(null);
    if (!examPreviewQuizData || String(examPreviewQuizData?._quiz_id) !== String(currentEffectiveQuizId)) {
      setLoadingExamPreview(true);
      try {
        const res = previewPaper ? { data: previewPaper } : await axiosInstance.get(`/newtests/api/form-renderer/${currentEffectiveQuizId}/?preview=true`);
        const data = res.data;
        const rawSections = Array.isArray(data) ? data : (data?.sections || []);
        const formattedSections = rawSections.map((sec, sIdx) => ({
          ...sec,
          questions: (sec.questions || []).map((q, qIdx) => ({
            ...q,
            question_number: qIdx,
          })),
        }));
        formattedSections._quiz_id = currentEffectiveQuizId;
        formattedSections._raw = data;
        setExamPreviewQuizData(formattedSections);
      } catch (err) {
        console.error('Error fetching exam preview quiz data:', err);
        setExamPreviewError(err?.response?.data?.message || err?.message || 'Failed to load exam preview.');
      } finally {
        setLoadingExamPreview(false);
      }
    }
  };

  const handleJumpToExamPreviewQuestion = (sIdx, qIdx) => {
    if (examPreviewSectionTab !== 'all' && examPreviewSectionTab !== sIdx) {
      setExamPreviewSectionTab('all');
    }
    setExamPreviewMobileNavOpen(false);
    setTimeout(() => {
      const el = document.getElementById(`exam-preview-question-${sIdx}-${qIdx}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.3s ease, border-color 0.3s ease';
        const origBorder = el.style.borderColor;
        el.style.borderColor = '#236B59';
        el.style.boxShadow = '0 0 0 4px rgba(35, 107, 89, 0.3)';
        setTimeout(() => {
          el.style.borderColor = origBorder;
          el.style.boxShadow = '';
        }, 1500);
      }
    }, 80);
  };

  const lastPropQuizIdRef = React.useRef(propQuizId);
  const lastPropIdRef = React.useRef(propId);

  // Synchronize quizList and selectedQuiz when propQuizzes is provided
  useEffect(() => {
    if (propQuizzes !== undefined) {
      const uniqueQuizzes = Array.from(
        new Map((propQuizzes || []).map((q) => [q.quiz_id || q.id, q])).values()
      );
      setQuizList(uniqueQuizzes);
      if (uniqueQuizzes.length > 0) {
        setSelectedQuiz((prev) => {
          if (propQuizId) {
            const matchedProp = uniqueQuizzes.find(
              (q) => String(q.quiz_id || q.id) === String(propQuizId)
            );
            if (matchedProp) return matchedProp;
          }
          if (effectiveResultId) {
            const matchedResult = uniqueQuizzes.find(
              (q) => String(q.result_id) === String(effectiveResultId)
            );
            if (matchedResult) return matchedResult;
          }
          if (prev && uniqueQuizzes.some((q) => String(q.quiz_id || q.id) === String(prev.quiz_id || prev.id))) {
            return prev;
          }
          return uniqueQuizzes[0];
        });
      } else {
        setSelectedQuiz(null);
      }
    }
  }, [propQuizzes, propQuizId, effectiveResultId]);

  // Synchronize selectedQuiz when parent passes changed propQuizId or propId
  useEffect(() => {
    const propQuizChanged = propQuizId && propQuizId !== lastPropQuizIdRef.current;
    const propIdChanged = propId && propId !== lastPropIdRef.current;

    if (propQuizChanged || propIdChanged) {
      lastPropQuizIdRef.current = propQuizId;
      lastPropIdRef.current = propId;

      if (quizList.length > 0) {
        let match = null;
        if (propQuizId) {
          match = quizList.find((q) => String(q.quiz_id || q.id) === String(propQuizId));
        }
        if (!match && propId) {
          match = quizList.find((q) => String(q.result_id) === String(propId));
        }
        if (match) {
          setSelectedQuiz(match);
        }
      }
    }
  }, [propQuizId, propId, quizList]);

  // Fetch available quizzes for the student if not supplied via propQuizzes
  useEffect(() => {
    if (propQuizzes !== undefined) return;

    let isMounted = true;
    const fetchQuizzes = async () => {
      try {
        setLoadingQuizzes(true);
        const response = await postFetcher([
          'courses/api/quizzes/by-course/',
          {
            student_id: targetStudentId,
            ...(effectiveIsTalentHunt ? { is_talent_hunt: true } : {}),
          },
        ]);
        if (!isMounted) return;
        const uniqueQuizzes = Array.from(
          new Map((response?.quizzes || []).map((q) => [q.quiz_id || q.id, q])).values()
        );
        setQuizList(uniqueQuizzes);
        if (uniqueQuizzes.length > 0) {
          setSelectedQuiz((prev) => {
            if (propQuizId) {
              const matchedProp = uniqueQuizzes.find((q) => String(q.quiz_id || q.id) === String(propQuizId));
              if (matchedProp) return matchedProp;
            }
            if (quizIdParam) {
              const matchedParam = uniqueQuizzes.find((q) => String(q.quiz_id || q.id) === String(quizIdParam));
              if (matchedParam) return matchedParam;
            }
            if (prev && uniqueQuizzes.some((q) => String(q.quiz_id || q.id) === String(prev.quiz_id || prev.id))) {
              return prev;
            }
            return uniqueQuizzes[0];
          });
        } else {
          setSelectedQuiz(null);
          setLoading(false);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error fetching quiz list:', err);
        setLoading(false);
      } finally {
        if (isMounted) {
          setLoadingQuizzes(false);
        }
      }
    };

    if (targetStudentId) {
      fetchQuizzes();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [targetStudentId, effectiveIsTalentHunt, propQuizzes, propQuizId, quizIdParam]);

  // Load report data
  useEffect(() => {
    if (suppliedReportData !== undefined) {
      setReportData(suppliedReportData);
      setLoading(false);
      setError(null);
      return;
    }
    const effectiveQuizId = selectedQuiz?.quiz_id || selectedQuiz?.id || effectiveQuizIdParam;

    if (effectiveResultId || effectiveQuizId) {
      setLoading(true);
      setError(null);

      // Determine result_id for current selection
      let activeResultId = selectedQuiz?.result_id;
      if (!activeResultId && effectiveResultId) {
        if (!selectedQuiz || String(selectedQuiz?.quiz_id || selectedQuiz?.id) === String(propQuizId)) {
          activeResultId = effectiveResultId;
        }
      }

      const payload = {
        ...(effectiveQuizId ? { quiz_id: effectiveQuizId } : {}),
        ...(activeResultId ? { result_id: activeResultId } : {}),
        ...(targetStudentId ? { student_id: targetStudentId } : {}),
        ...(effectiveIsTalentHunt ? { is_talent_hunt: true } : {}),
      };

      axiosInstance
        .post('/courses/api/student/analysis/report/detail/', payload)
        .then((response) => {
          if (response.data) {
            setReportData(response.data);
          }
        })
        .catch((err) => {
          console.error('Error fetching student analysis report detail:', err);
          setError(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.error ||
              err?.message ||
              'Failed to load report data'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (!loadingQuizzes) {
      setLoading(false);
    }
  }, [
    suppliedReportData,
    effectiveResultId,
    selectedQuiz?.quiz_id,
    selectedQuiz?.id,
    selectedQuiz?.result_id,
    effectiveQuizIdParam,
    targetStudentId,
    loadingQuizzes,
    effectiveIsTalentHunt,
    propQuizId,
  ]);

  // Synchronize selectedQuiz with current reportData or URL param if not yet selected
  useEffect(() => {
    if (!selectedQuiz && quizList.length > 0) {
      const currentQuizId = propQuizId || reportData?.quiz_id || quizIdParam;
      if (currentQuizId) {
        const match = quizList.find((q) => String(q.quiz_id || q.id) === String(currentQuizId));
        if (match) {
          setSelectedQuiz(match);
        }
      }
    }
  }, [propQuizId, reportData?.quiz_id, quizIdParam, quizList, selectedQuiz]);

  // Handle Quiz Dropdown Change
  const handleQuizChange = (event, newQuiz) => {
    if (!newQuiz) return;
    setSelectedQuiz(newQuiz);
    if (isEmbedded) {
      return;
    }
    const newQuizId = newQuiz.quiz_id || newQuiz.id;
    const currentParams = new URLSearchParams(searchParams.toString());
    currentParams.set('quiz_id', newQuizId);
    if (studentIdParam) {
      currentParams.set('student_id', studentIdParam);
    }
    currentParams.set('report_type', activeTab);
    if (effectiveIsTalentHunt) {
      currentParams.set('is_talent_hunt', 'true');
    }

    // If we have a direct result_id from newQuiz
    const nextResultId = newQuiz.result_id || newQuiz.id;
    if (nextResultId && nextResultId !== newQuizId) {
      const targetBase = isParent
        ? paths.parentdashboard.examinationresult.profile(nextResultId)
        : isHeightStudent
        ? paths.heightstudentdashboard.examinationresult.profile(nextResultId)
        : isStudent
        ? paths.studentdashboard.examinationresult.profile(nextResultId)
        : paths.dashboard.examinationresult.profile(nextResultId);
      router.push(`${targetBase}?${currentParams.toString()}`);
    } else {
      const targetBase = isParent
        ? `${paths.parentdashboard.examinationresult.root}/preview/profile`
        : isHeightStudent
        ? `${paths.heightstudentdashboard.examinationresult.root}/preview/profile`
        : isStudent
        ? `${paths.studentdashboard.examinationresult.root}/preview/profile`
        : `${paths.dashboard.examinationresult.root}/preview/profile`;
      router.push(`${targetBase}?${currentParams.toString()}`);
    }
  };

  // Extract Student Info
  const studentInfo = reportData?.student_info || {};
  const studentName = studentInfo?.name || 'Student';
  const rollNo = studentInfo?.roll_no || 'N/A';
  const batchName = studentInfo?.batch || 'N/A';
  const testType = studentInfo?.test_type || 'Paper-I';
  const testName = studentInfo?.test_name || 'Exam';

  // Extract Subject Summary
  const subjectSummary = reportData?.subject_summary || [];
  const totalSummary =
    reportData?.total_summary ||
    subjectSummary.reduce(
      (acc, curr) => ({
        totalQues: acc.totalQues + (curr.totalQues || 0),
        correct: acc.correct + (curr.correct || 0),
        wrong: acc.wrong + (curr.wrong || 0),
        unattempted: acc.unattempted + (curr.unattempted || 0),
        marksObtained: acc.marksObtained + (Number(curr.marksObtained) || 0),
      }),
      { totalQues: 0, correct: 0, wrong: 0, unattempted: 0, marksObtained: 0 }
    );

  // Extract Question Wise Data with Continuous Question Numbering across subjects
  const rawQuestionWiseData = reportData?.question_wise_details || {};
  const questionWiseData = useMemo(() => {
    const subjects = Object.keys(rawQuestionWiseData);
    if (subjects.length === 0) return {};
    const sortedRawData = {};
    subjects.forEach((subj) => {
      sortedRawData[subj] = [...(rawQuestionWiseData[subj] || [])].sort((a, b) => Number(a.qNo) - Number(b.qNo));
    });
    let runningQNo = 0;
    let alreadyContinuous = true;

    for (let sIdx = 0; sIdx < subjects.length; sIdx++) {
      const list = sortedRawData[subjects[sIdx]] || [];
      if (list.length > 0 && sIdx > 0) {
        const firstQ = Number(list[0]?.qNo || 0);
        if (firstQ <= runningQNo) {
          alreadyContinuous = false;
          break;
        }
      }
      runningQNo += list.length;
    }

    if (alreadyContinuous) {
      return sortedRawData;
    }

    const continuousDetails = {};
    let continuousCount = 0;
    subjects.forEach((subj) => {
      const list = sortedRawData[subj] || [];
      continuousDetails[subj] = list.map((item) => {
        continuousCount += 1;
        return {
          ...item,
          qNo: continuousCount,
        };
      });
    });
    return continuousDetails;
  }, [rawQuestionWiseData]);

  const subjectsList = useMemo(() => Object.keys(questionWiseData), [questionWiseData]);
  const maxQuestionsInSubject = Math.max(
    ...subjectsList.map((s) =>
      Array.isArray(questionWiseData[s]) ? questionWiseData[s].length : 0
    ),
    0
  );

  // Extract Sub-Reports Data
  const testResult = reportData?.test_result || {
    rank: '-',
    total_students: '-',
    percentile: '-',
    scored_marks: formatMarks(totalSummary?.marksObtained),
    total_marks: '-',
    percentage: '-',
    subjects: [],
  };

  const quizSubjectMarks = reportData?.quiz_subject_marks || {
    quiz_name: testName,
    mode: testType,
    date: '',
    scored_marks: formatMarks(totalSummary.marksObtained),
    total_marks: '0',
    percentage: 0,
    subjects: [],
  };

  const reportCard = reportData?.report_card || {
    student_name: studentName,
    roll_no: rollNo,
    batch: batchName,
    tests_taken: 1,
    grand_scored: formatMarks(totalSummary.marksObtained),
    grand_total: '0',
    overall_percentage: 0,
    tests: [],
  };

  // Graphical calculations
  const attemptedTotal = (totalSummary.correct || 0) + (totalSummary.wrong || 0);
  const accuracyPct =
    attemptedTotal > 0
      ? Math.round(((totalSummary.correct || 0) / attemptedTotal) * 100)
      : 0;

  // Chart: Attempt Donut
  const pieSeries = [
    totalSummary.correct || 0,
    totalSummary.wrong || 0,
    totalSummary.unattempted || 0,
  ];
  const pieOptions = useChart({
    chart: { sparkline: { enabled: false } },
    colors: [
      theme.palette.success.main,
      theme.palette.error.main,
      theme.palette.warning.main,
    ],
    labels: ['Correct', 'Wrong', 'Unattempted'],
    stroke: { colors: [theme.palette.background.paper] },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
    },
    dataLabels: {
      enabled: true,
      dropShadow: { enabled: false },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '68%',
          labels: {
            show: true,
            value: {
              formatter: (val) => String(val),
            },
            total: {
              show: true,
              label: 'Total Questions',
              formatter: () => String(totalSummary.totalQues || 0),
            },
          },
        },
      },
    },
  });

  // Chart: Subject Breakdown Stacked Bar
  const barCategories = subjectSummary.map((s) => s.subject);
  const barSeries = [
    {
      name: 'Correct',
      data: subjectSummary.map((s) => s.correct || 0),
    },
    {
      name: 'Wrong',
      data: subjectSummary.map((s) => s.wrong || 0),
    },
    {
      name: 'Unattempted',
      data: subjectSummary.map((s) => s.unattempted || 0),
    },
  ];
  const barOptions = useChart({
    chart: { stacked: true },
    colors: [
      theme.palette.success.main,
      theme.palette.error.main,
      theme.palette.warning.main,
    ],
    stroke: { show: false },
    xaxis: {
      categories: barCategories,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
    },
    plotOptions: {
      bar: {
        columnWidth: '40%',
        borderRadius: 4,
      },
    },
  });

  // Chart: Subject Marks Distribution
  const marksCategories = subjectSummary.map((s) => s.subject);
  const marksSeries = [
    {
      name: 'Marks Scored',
      data: subjectSummary.map((s) => Number(s.marksObtained || 0)),
    },
  ];
  const marksOptions = useChart({
    colors: [theme.palette.primary.main],
    stroke: { show: false },
    xaxis: {
      categories: marksCategories,
    },
    plotOptions: {
      bar: {
        columnWidth: '35%',
        borderRadius: 4,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '11px',
        colors: [theme.palette.text.primary],
      },
      formatter: (val) => formatMarks(val),
    },
  });

  // Comparative Marks (Student vs Topper vs Average)
  const comparativeMarks = reportData?.comparative_marks || {
    student_marks: Number(totalSummary.marksObtained || 0),
    topper_marks: Number(totalSummary.marksObtained || 0),
    average_marks: Number(totalSummary.marksObtained || 0),
    max_marks: Number(testResult?.total_marks || 0),
    subjects: subjectSummary.map((s) => ({
      subject: s.subject,
      student_marks: Number(s.marksObtained || 0),
      topper_marks: Number(s.marksObtained || 0),
      average_marks: Number(s.marksObtained || 0),
    })),
  };

  const compSubjects = comparativeMarks.subjects && comparativeMarks.subjects.length > 0
    ? comparativeMarks.subjects
    : subjectSummary.map((s) => ({
        subject: s.subject,
        student_marks: Number(s.marksObtained || 0),
        topper_marks: Number(s.marksObtained || 0),
        average_marks: Number(s.marksObtained || 0),
      }));

  // Chart: Comparative Subject Performance (Student vs Topper vs Average)
  const compCategories = compSubjects.map((s) => s.subject);
  const compSeries = [
    {
      name: 'Your Marks',
      data: compSubjects.map((s) => Number(s.student_marks || 0)),
    },
    {
      name: 'Topper Marks',
      data: compSubjects.map((s) => Number(s.topper_marks || 0)),
    },
    {
      name: 'Average Marks',
      data: compSubjects.map((s) => Number(s.average_marks || 0)),
    },
  ];

  const compOptions = useChart({
    colors: [
      theme.palette.info.main,
      theme.palette.success.main,
      theme.palette.warning.main,
    ],
    stroke: { show: true, width: 2, colors: ['transparent'] },
    xaxis: {
      categories: compCategories,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '45%',
        borderRadius: 4,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '11px',
        colors: [theme.palette.text.primary],
      },
      formatter: (val) => formatMarks(val),
    },
    tooltip: {
      y: {
        formatter: (val) => `${formatMarks(val)} Marks`,
      },
    },
  });

  // Chart: Total Score Benchmark (Overall: Student vs Topper vs Average)
  const compTotalCategories = ['Total Score'];
  const compTotalSeries = [
    {
      name: 'Your Score',
      data: [Number(comparativeMarks.student_marks || totalSummary.marksObtained || 0)],
    },
    {
      name: 'Topper Score',
      data: [Number(comparativeMarks.topper_marks || totalSummary.marksObtained || 0)],
    },
    {
      name: 'Average Score',
      data: [Number(comparativeMarks.average_marks || totalSummary.marksObtained || 0)],
    },
  ];

  const compTotalOptions = useChart({
    colors: [
      theme.palette.info.main,
      theme.palette.success.main,
      theme.palette.warning.main,
    ],
    stroke: { show: true, width: 2, colors: ['transparent'] },
    xaxis: {
      categories: compTotalCategories,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '40%',
        borderRadius: 6,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: [theme.palette.text.primary],
      },
      formatter: (val) => formatMarks(val),
    },
    tooltip: {
      y: {
        formatter: (val) => `${formatMarks(val)} Marks`,
      },
    },
  });

  // Unified Subject Performance Rows merging all 4 reports
  const mergedSubjectSummary = subjectSummary.map((s) => {
    const trMatch = (testResult.subjects || []).find((ts) => ts.subject === s.subject);
    const qmMatch = (quizSubjectMarks.subjects || []).find((qs) => qs.subject === s.subject);
    const totalMarks = trMatch?.total_marks || qmMatch?.total_marks || ((s.totalQues || 0) * 4);
    const marksScored = s.marksObtained;
    const percentage =
      trMatch?.percentage ??
      qmMatch?.percentage ??
      (Number(totalMarks) > 0 ? Math.round((Number(marksScored) / Number(totalMarks)) * 100) : 0);
    const attempted = (s.correct || 0) + (s.wrong || 0);
    const accuracy = attempted > 0 ? Math.round(((s.correct || 0) / attempted) * 100) : 0;
    return {
      ...s,
      attempted,
      accuracy,
      totalMarks,
      percentage,
    };
  });

  // Derive comprehensive time analysis
  const timeAnalysis = useMemo(() => {
    if (reportData?.time_analysis && reportData.time_analysis.total_time_spent > 0) {
      return reportData.time_analysis;
    }

    let totalTime = 0;
    let correctTime = 0;
    let correctCount = 0;
    let wrongTime = 0;
    let wrongCount = 0;
    let unattemptedTime = 0;
    let unattemptedCount = 0;
    let totalQuestions = 0;
    const subjectTimeAnalysis = [];
    let fastestCorrect = null;
    let slowestQuestion = null;

    subjectsList.forEach((subj) => {
      const qList = questionWiseData[subj] || [];
      let sTime = 0;
      let sCorrectTime = 0;
      let sCorrectCount = 0;
      let sWrongTime = 0;
      let sWrongCount = 0;
      let sUnattemptedTime = 0;
      let sUnattemptedCount = 0;

      qList.forEach((q) => {
        totalQuestions += 1;
        const isU = q.sa === '-' || q.sa === 'Not Attempted';
        const rawT = Number(q.time_spent) || 0;
        const t = !isU && rawT <= 0 ? 1 : rawT;
        sTime += t;
        if (q.isCorrect) {
          sCorrectTime += t;
          sCorrectCount += 1;
          correctTime += t;
          correctCount += 1;
          if (t > 0 && (!fastestCorrect || t < fastestCorrect.time_spent)) {
            fastestCorrect = { qNo: q.qNo, subject: subj, time_spent: t };
          }
        } else if (isU) {
          sUnattemptedTime += t;
          sUnattemptedCount += 1;
          unattemptedTime += t;
          unattemptedCount += 1;
        } else {
          sWrongTime += t;
          sWrongCount += 1;
          wrongTime += t;
          wrongCount += 1;
        }

        if (t > 0 && (!slowestQuestion || t > slowestQuestion.time_spent)) {
          slowestQuestion = {
            qNo: q.qNo,
            subject: subj,
            time_spent: t,
            isCorrect: q.isCorrect,
            isUnattempted: isU,
          };
        }
      });

      totalTime += sTime;
      const sLen = qList.length;
      subjectTimeAnalysis.push({
        subject: subj,
        total_time: sTime,
        avg_time: sLen > 0 ? Math.round(sTime / sLen) : 0,
        correct_time: sCorrectTime,
        avg_time_correct: sCorrectCount > 0 ? Math.round(sCorrectTime / sCorrectCount) : 0,
        wrong_time: sWrongTime,
        avg_time_wrong: sWrongCount > 0 ? Math.round(sWrongTime / sWrongCount) : 0,
        unattempted_time: sUnattemptedTime,
        avg_time_unattempted: sUnattemptedCount > 0 ? Math.round(sUnattemptedTime / sUnattemptedCount) : 0,
      });
    });

    const attemptedTime = correctTime + wrongTime;
    const attemptedCount = correctCount + wrongCount;

    return {
      total_time_spent: totalTime,
      avg_time_per_question: totalQuestions > 0 ? Math.round(totalTime / totalQuestions) : 0,
      avg_time_attempted: attemptedCount > 0 ? Math.round(attemptedTime / attemptedCount) : 0,
      avg_time_correct: correctCount > 0 ? Math.round(correctTime / correctCount) : 0,
      avg_time_wrong: wrongCount > 0 ? Math.round(wrongTime / wrongCount) : 0,
      avg_time_unattempted: unattemptedCount > 0 ? Math.round(unattemptedTime / unattemptedCount) : 0,
      total_correct_time: correctTime,
      total_wrong_time: wrongTime,
      total_unattempted_time: unattemptedTime,
      subject_time_analysis: subjectTimeAnalysis,
      fastest_correct: fastestCorrect,
      slowest_question: slowestQuestion,
    };
  }, [reportData?.time_analysis, questionWiseData, subjectsList]);

  // Flattened question list for Question-by-Question charts
  const allQuestionsFlattened = useMemo(() => {
    const list = [];
    subjectsList.forEach((subj) => {
      const qList = questionWiseData[subj] || [];
      qList.forEach((q) => {
        list.push({ ...q, subject: subj });
      });
    });
    return list.sort((a, b) => Number(a.qNo) - Number(b.qNo));
  }, [questionWiseData, subjectsList]);

  // Time Chart 1: Time Spent by Outcome Donut Chart
  const timeOutcomeSeries = [
    timeAnalysis.total_correct_time || 0,
    timeAnalysis.total_wrong_time || 0,
    timeAnalysis.total_unattempted_time || 0,
  ];
  const timeOutcomeOptions = useChart({
    chart: { sparkline: { enabled: false } },
    colors: [
      theme.palette.success.main,
      theme.palette.error.main,
      theme.palette.text.disabled,
    ],
    labels: ['Correct Attempts', 'Incorrect Attempts', 'Unattempted / Skipped'],
    stroke: { colors: [theme.palette.background.paper] },
    legend: { position: 'bottom', horizontalAlign: 'center' },
    tooltip: {
      fillSeriesColor: false,
      y: {
        formatter: (val) => formatTimeSpent(val),
        title: { formatter: (seriesName) => `${seriesName}: ` },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            value: {
              formatter: (val) => formatTimeSpent(val),
            },
            total: {
              show: true,
              label: 'Total Time',
              formatter: () => formatTimeSpent(timeAnalysis.total_time_spent),
            },
          },
        },
      },
    },
  });

  // Time Chart 2: Subject-Wise Time Allocation Stacked Bar Chart
  const subjectTimeCategories = timeAnalysis.subject_time_analysis.map((s) => s.subject);
  const subjectTimeSeries = [
    {
      name: 'Correct Time',
      data: timeAnalysis.subject_time_analysis.map((s) => s.correct_time || 0),
    },
    {
      name: 'Incorrect Time',
      data: timeAnalysis.subject_time_analysis.map((s) => s.wrong_time || 0),
    },
    {
      name: 'Unattempted Time',
      data: timeAnalysis.subject_time_analysis.map((s) => s.unattempted_time || 0),
    },
  ];
  const subjectTimeOptions = useChart({
    chart: { stacked: true },
    colors: [
      theme.palette.success.main,
      theme.palette.error.main,
      theme.palette.text.disabled,
    ],
    stroke: { show: false },
    xaxis: {
      categories: subjectTimeCategories,
    },
    yaxis: {
      labels: {
        formatter: (val) => formatTimeSpent(val),
      },
    },
    tooltip: {
      y: {
        formatter: (val) => formatTimeSpent(val),
      },
    },
    legend: { position: 'bottom', horizontalAlign: 'center' },
  });

  // Time Chart 3: Question-by-Question Time Distribution Bar Chart
  const qTimeCategories = allQuestionsFlattened.map((q) => `Q${q.qNo}`);
  const qTimeSeries = [
    {
      name: 'Time Spent',
      data: allQuestionsFlattened.map((q) => {
        const isU = q.sa === '-' || q.sa === 'Not Attempted';
        const rawT = Number(q.time_spent) || 0;
        return !isU && rawT <= 0 ? 1 : rawT;
      }),
    },
  ];
  const qTimeOptions = useChart({
    chart: { toolbar: { show: true } },
    plotOptions: {
      bar: {
        columnWidth: '60%',
        distributed: true,
      },
    },
    colors: allQuestionsFlattened.map((q) => {
      const isU = q.sa === '-' || q.sa === 'Not Attempted';
      if (q.isCorrect) return theme.palette.success.main;
      if (isU) return theme.palette.text.disabled;
      return theme.palette.error.main;
    }),
    xaxis: {
      categories: qTimeCategories,
      labels: {
        rotate: -45,
        style: { fontSize: '10px' },
      },
    },
    yaxis: {
      title: { text: 'Time Spent (seconds)' },
      labels: {
        formatter: (val) => formatTimeSpent(val),
      },
    },
    tooltip: {
      custom: ({ series, seriesIndex, dataPointIndex }) => {
        const q = allQuestionsFlattened[dataPointIndex];
        if (!q) return '';
        const isU = q.sa === '-' || q.sa === 'Not Attempted';
        const rawT = Number(q.time_spent) || 0;
        const displayTime = !isU && rawT <= 0 ? 1 : rawT;
        const statusText = q.isCorrect ? 'Correct' : isU ? 'Unattempted' : 'Incorrect';
        const statusColor = q.isCorrect ? '#2e7d32' : isU ? '#757575' : '#d32f2f';
        return `
          <div style="padding: 10px; font-family: inherit; font-size: 12px; background: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); color: #212b36;">
            <div style="font-weight: bold; margin-bottom: 4px;">Q${q.qNo} (${q.subject})</div>
            <div>Time Spent: <b>${formatTimeSpent(displayTime)}</b></div>
            <div>Status: <span style="color: ${statusColor}; font-weight: bold;">${statusText}</span></div>
            <div>Student Attempt: <b>${q.sa}</b> | Ans Key: <b>${q.ak}</b></div>
          </div>
        `;
      },
    },
    annotations: {
      yaxis: timeAnalysis.avg_time_per_question > 0 ? [
        {
          y: timeAnalysis.avg_time_per_question,
          borderColor: theme.palette.info.main,
          strokeDashArray: 4,
          label: {
            borderColor: theme.palette.info.main,
            style: { color: '#fff', background: theme.palette.info.main },
            text: `Avg: ${formatTimeSpent(timeAnalysis.avg_time_per_question)}`,
          },
        },
      ] : [],
    },
    legend: { show: false },
  });

  // Multi-test history for Student Report Card
  const reportCardTests = reportCard.tests || [];

  // REPORT CARD GRAPHICAL REPRESENTATION: Details of the LAST 10 GIVEN QUIZZES
  const last10Tests = reportCardTests.slice(0, 10);
  const chartTests = [...last10Tests].reverse(); // Chronological progression (oldest to newest of the last 10)

  // Chart 1: Test Percentage Score Performance Trend (Last 10 given quizzes)
  const trendCategories = chartTests.map((t) => t.quiz_name || `Test ${t.s_no}`);
  const trendSeries = [
    {
      name: 'Percentage Score (%)',
      data: chartTests.map((t) => Number(t.percentage || 0)),
    },
  ];
  const trendOptions = useChart({
    colors: [theme.palette.info.main],
    stroke: { curve: 'smooth', width: 3 },
    xaxis: {
      categories: trendCategories,
      labels: {
        rotate: -25,
        rotateAlways: trendCategories.length > 3,
        style: { fontSize: '11px' },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
    },
  });

  // Chart 2: Test-by-Test Scored vs Total Marks Bar Chart (Last 10 given quizzes)
  const testMarksSeries = [
    {
      name: 'Marks Scored',
      data: chartTests.map((t) => Number(t.scored_marks || 0)),
    },
    {
      name: 'Total Marks',
      data: chartTests.map((t) => Number(t.total_marks || 0)),
    },
  ];
  const testMarksOptions = useChart({
    colors: [theme.palette.primary.main, theme.palette.grey[400]],
    stroke: { show: true, width: 2, colors: ['transparent'] },
    xaxis: {
      categories: trendCategories,
      labels: {
        rotate: -25,
        rotateAlways: trendCategories.length > 3,
        style: { fontSize: '11px' },
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '45%',
        borderRadius: 4,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '11px',
        colors: [theme.palette.text.primary],
      },
      formatter: (val) => formatMarks(val),
    },
  });

  // Chart 3: Test Rank Progression Chart (Last 10 given quizzes)
  const rankSeries = [
    {
      name: 'Class Rank',
      data: chartTests.map((t) => Number(t.rank || 1)),
    },
  ];
  const rankOptions = useChart({
    colors: [theme.palette.warning.main],
    stroke: { curve: 'straight', width: 3 },
    markers: { size: 5 },
    xaxis: {
      categories: trendCategories,
      labels: {
        rotate: -25,
        rotateAlways: trendCategories.length > 3,
        style: { fontSize: '11px' },
      },
    },
    yaxis: {
      reversed: true, // Lower number is better rank
      labels: {
        formatter: (val) => `#${Math.round(val)}`,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `#${val}`,
    },
  });

  // Resolve Header Title
  const getHeaderTitle = () => {
    switch (activeTab) {
      case 'report_card':
        return 'Student Report Card Analysis';
      default:
        return 'Student Test Analysis Report';
    }
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'} disableGutters={isEmbedded} sx={{ px: isEmbedded ? 0 : undefined }}>
      {/* Top Breadcrumbs & Back Navigation */}
      {!isEmbedded && (
        <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
          <CustomBreadcrumbs
            heading={getHeaderTitle()}
            links={[
              { name: 'Dashboard', href: resolvedDashboardRoot },
              { name: 'Result List', href: resolvedResultList },
              { name: 'Report View' },
            ]}
            action={
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<Iconify icon="solar:arrow-left-bold" />}
                onClick={() => router.push(resolvedResultList)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Back to Results
              </Button>
            }
          />
        </Box>
      )}

      {/* Report Switcher Tabs & Quiz Selector */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            px: 2,
            py: 1,
            gap: 2,
          }}
        >
          <Tabs
            value={activeTab === 'report_card' ? 'report_card' : 'test_analysis'}
            onChange={(e, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flexGrow: 1,
              '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600 },
            }}
          >
            <Tab
              value="test_analysis"
              label="Test Analysis"
              icon={<Iconify icon="solar:chart-square-bold" width={20} />}
              iconPosition="start"
            />
            {!practiceMode && (
            <Tab
              value="report_card"
              label="Report Card"
              icon={<Iconify icon="solar:target-bold" width={20} />}
              iconPosition="start"
            />
            )}
          </Tabs>

          {/* Quiz Selection Dropdown & Preview Exam Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}>
            {!practiceMode && (
            <Box sx={{ minWidth: { xs: '100%', sm: 260, md: 300 } }}>
              <Autocomplete
                size="small"
                options={quizList}
                getOptionLabel={(option) =>
                  option?.quiz_name || option?.name || `Quiz #${option?.quiz_id || option?.id}`
                }
                isOptionEqualToValue={(option, value) =>
                  String(option?.quiz_id || option?.id) === String(value?.quiz_id || value?.id)
                }
                value={selectedQuiz}
                loading={loadingQuizzes}
                onChange={handleQuizChange}
                renderInput={(inputParams) => (
                  <TextField
                    {...inputParams}
                    label="Select Quiz / Exam"
                    placeholder="Switch Exam..."
                    InputProps={{
                      ...inputParams.InputProps,
                      endAdornment: (
                        <>
                          {loadingQuizzes ? <CircularProgress color="inherit" size={18} /> : null}
                          {inputParams.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
            )}

            {canShowExamPreview && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenExamPreview}
                disabled={!currentEffectiveQuizId}
                startIcon={<Iconify icon="solar:eye-bold" width={18} />}
                sx={{
                  bgcolor: '#236B59',
                  '&:hover': { bgcolor: '#1B5244' },
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '8px',
                  height: 40,
                  px: 2,
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                }}
              >
                Preview Exam
              </Button>
            )}
          </Box>
        </Box>
      </Card>

      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={12}
        >
          <CircularProgress size={48} />
          <Typography variant="subtitle1" sx={{ mt: 2.5, color: 'text.secondary' }}>
            Loading Examination Report...
          </Typography>
        </Box>
      ) : error ? (
        <Alert
          severity={String(error).toLowerCase().includes('not found') ? 'info' : 'error'}
          action={
            !isEmbedded && (
              <Button
                color="inherit"
                size="small"
                onClick={() => router.push(resolvedResultList)}
              >
                Go Back
              </Button>
            )
          }
          sx={{ my: 4 }}
        >
          {String(error).toLowerCase().includes('not found') ? 'No examination records found for this student.' : error}
        </Alert>
      ) : !reportData && quizList.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center', my: 4 }}>
          <Iconify icon="solar:document-text-outline" width={48} sx={{ color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary">
            No Examination Reports Found
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            This student has not appeared in any examinations yet.
          </Typography>
        </Card>
      ) : (
        <>
          {/* ========================================================================= */}
          {/* TAB 1: TEST ANALYSIS (UNIFIED UNIQUE DATA FROM ALL FOUR ORIGINAL REPORTS)   */}
          {/* ========================================================================= */}
          {activeTab !== 'report_card' && (
            <Box>
              {/* 1. Top KPI Summary Cards (Unified from Tabs 2 & 4) */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'primary.lighter',
                      borderColor: 'primary.light',
                    }}
                  >
                    <Typography variant="caption" color="primary.dark" fontWeight={600}>
                      Total Score
                    </Typography>
                    <Typography variant="h5" color="primary.darker" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {formatMarks(testResult.scored_marks || totalSummary.marksObtained)}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'warning.lighter',
                      borderColor: 'warning.light',
                    }}
                  >
                    <Typography variant="caption" color="warning.dark" fontWeight={600}>
                      Percentage
                    </Typography>
                    <Typography variant="h5" color="warning.darker" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {testResult?.percentage || '-'}%
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'secondary.lighter',
                      borderColor: 'secondary.light',
                    }}
                  >
                    <Typography variant="caption" color="secondary.dark" fontWeight={600}>
                      {practiceMode ? 'Attempt' : 'Class Rank'}
                    </Typography>
                    <Typography variant="h5" color="secondary.darker" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {practiceMode ? suppliedReportData?.attempt_number : `#${testResult.rank || 1}`}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={3}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (t) => t.palette.background.neutral || '#f8f9fa',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Attempted
                    </Typography>
                    <Typography variant="h5" color="text.primary" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {attemptedTotal} / {totalSummary.totalQues || 0}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* 2. Student & Examination Information Card */}
              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  bgcolor: (t) => t.palette.background.neutral || '#f8f9fa',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Student Name
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {studentName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={6} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        Roll Number
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {rollNo}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={6} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        Batch
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {batchName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Examination Name
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {testName} 
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        Exam Date
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {quizSubjectMarks?.date || '-'}
                        
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* 3. Performance & Benchmark Charts (Unified from Tabs 1, 2, 3, 4) */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={4}>
                    <Card elevation={1} sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Attempt Overview
                      </Typography>
                      <Chart
                        dir="ltr"
                        type="donut"
                        series={pieSeries}
                        options={pieOptions}
                        height={260}
                      />
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card elevation={1} sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Subject Question Breakdown
                      </Typography>
                      <Chart
                        dir="ltr"
                        type="bar"
                        series={barSeries}
                        options={barOptions}
                        height={260}
                      />
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card elevation={1} sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Subject Marks Distribution
                      </Typography>
                      <Chart
                        dir="ltr"
                        type="bar"
                        series={marksSeries}
                        options={marksOptions}
                        height={260}
                      />
                    </Card>
                  </Grid>

                  {!practiceMode && <>
                  <Grid item xs={12} md={8}>
                    <Card elevation={1} sx={{ p: 2, height: '100%' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Subject Score Benchmark: Student vs Topper vs Average
                        </Typography>
                        <Stack direction="row" spacing={1.5}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'info.main' }} />
                            <Typography variant="caption">Student</Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
                            <Typography variant="caption">Topper</Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'warning.main' }} />
                            <Typography variant="caption">Average</Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Chart
                        dir="ltr"
                        type="bar"
                        series={compSeries}
                        options={compOptions}
                        height={260}
                      />
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card elevation={1} sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Overall Score Benchmark
                      </Typography>
                      <Chart
                        dir="ltr"
                        type="bar"
                        series={compTotalSeries}
                        options={compTotalOptions}
                        height={260}
                      />
                    </Card>
                  </Grid>
                  </>}
                </Grid>
              </Box>

              {/* 4. Unified Subject-Wise Performance Table */}
              <Card sx={{ p: 2.5, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Subject-Wise Performance Evaluation
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: (t) => t.palette.background.neutral || '#f4f6f8' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Total Ques</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Attempted</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Correct</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Wrong</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Unattempted</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Marks Scored</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Total Marks</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, minWidth: 120 }}>Percentage</TableCell>
                        {/* <TableCell align="center" sx={{ fontWeight: 700, minWidth: 120 }}>Accuracy</TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mergedSubjectSummary.map((row) => (
                        <TableRow key={row.subject} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{row.subject}</TableCell>
                          <TableCell align="center">{row.totalQues}</TableCell>
                          <TableCell align="center">{row.attempted}</TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={row.correct}
                              color="success"
                              variant="soft"
                              sx={{ minWidth: 32, fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={row.wrong}
                              color="error"
                              variant="soft"
                              sx={{ minWidth: 32, fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={row.unattempted}
                              variant="soft"
                              sx={{ minWidth: 32, fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {formatMarks(row.marksObtained)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>
                            {formatMarks(row.totalMarks)}
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" alignItems="center" gap={1}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(Math.max(Number(row.percentage) || 0, 0), 100)}
                                color={
                                  Number(row.percentage) >= 60
                                    ? 'success'
                                    : Number(row.percentage) >= 40
                                    ? 'warning'
                                    : 'error'
                                }
                                sx={{ flexGrow: 1, height: 6, borderRadius: 1 }}
                              />
                              <Typography variant="caption" sx={{ minWidth: 35, fontWeight: 600 }}>
                                {row.percentage}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Total / Summary Row */}
                      <TableRow
                        sx={{
                          bgcolor: (t) => t.palette.action.hover || '#fafafa',
                          '& td': { fontWeight: 'bold' },
                        }}
                      >
                        <TableCell>Total</TableCell>
                        <TableCell align="center">{totalSummary.totalQues}</TableCell>
                        <TableCell align="center">{attemptedTotal}</TableCell>
                        <TableCell align="center">{totalSummary.correct}</TableCell>
                        <TableCell align="center">{totalSummary.wrong}</TableCell>
                        <TableCell align="center">{totalSummary.unattempted}</TableCell>
                        <TableCell align="center" sx={{ color: 'primary.main', fontSize: '13px' }}>
                          {formatMarks(totalSummary.marksObtained)}
                        </TableCell>
                        <TableCell align="center">
                          {formatMarks(testResult.total_marks || totalSummary.totalQues * 4)}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(Math.max(Number(testResult.percentage || 0), 0), 100)}
                              color="primary"
                              sx={{ flexGrow: 1, height: 6, borderRadius: 1 }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 35, fontWeight: 'bold' }}>
                              {testResult.percentage || 0}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>

              {/* 5. Time Spent Analysis & Pacing Insights Section */}
              <Card sx={{ p: 2.5, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Iconify icon="solar:stopwatch-bold" width={24} sx={{ color: 'primary.main' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Time Spent Analysis & Pacing Insights
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Comprehensive breakdown of time invested per question, subject pacing, and efficiency by attempt outcome.
                    </Typography>
                  </Box>
                </Box>

                {/* 5a. Top Time KPI Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: (t) => t.palette.background.neutral || '#f8f9fa',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Total Time Spent
                      </Typography>
                      <Typography variant="h5" color="text.primary" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {formatTimeSpent(timeAnalysis.total_time_spent)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '10px' }}>
                        Across all questions
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: 'info.lighter',
                        borderColor: 'info.light',
                      }}
                    >
                      <Typography variant="caption" color="info.darker" fontWeight={600}>
                        Avg Time / Question
                      </Typography>
                      <Typography variant="h5" color="info.darker" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {formatTimeSpent(timeAnalysis.avg_time_per_question)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'info.dark', fontSize: '10px' }}>
                        All {totalSummary.totalQues} questions
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: 'success.lighter',
                        borderColor: 'success.light',
                      }}
                    >
                      <Typography variant="caption" color="success.darker" fontWeight={600}>
                        Avg Time on Correct
                      </Typography>
                      <Typography variant="h5" color="success.darker" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {formatTimeSpent(timeAnalysis.avg_time_correct)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'success.dark', fontSize: '10px' }}>
                        Total: {formatTimeSpent(timeAnalysis.total_correct_time)}
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: 'error.lighter',
                        borderColor: 'error.light',
                      }}
                    >
                      <Typography variant="caption" color="error.darker" fontWeight={600}>
                        Avg Time on Wrong
                      </Typography>
                      <Typography variant="h5" color="error.darker" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {formatTimeSpent(timeAnalysis.avg_time_wrong)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'error.dark', fontSize: '10px' }}>
                        Total: {formatTimeSpent(timeAnalysis.total_wrong_time)}
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: (t) => t.palette.action.hover || '#f4f6f8',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Avg Time on Skipped
                      </Typography>
                      <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {formatTimeSpent(timeAnalysis.avg_time_unattempted)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '10px' }}>
                        Total: {formatTimeSpent(timeAnalysis.total_unattempted_time)}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* 5b. Visual Charts: Time Outcome Donut & Subject Time Allocation */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={5}>
                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Time Distribution by Question Outcome
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Proportion of test time spent on correct vs incorrect vs skipped questions
                      </Typography>
                      {timeAnalysis.total_time_spent > 0 ? (
                        <Chart
                          type="donut"
                          series={timeOutcomeSeries}
                          options={timeOutcomeOptions}
                          height={280}
                        />
                      ) : (
                        <Box display="flex" alignItems="center" justifyContent="center" height={220}>
                          <Typography variant="caption" color="text.disabled">No timing data recorded</Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Subject-Wise Time Allocation
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Cumulative minutes spent across each subject broken down by correctness
                      </Typography>
                      {timeAnalysis.total_time_spent > 0 ? (
                        <Chart
                          type="bar"
                          series={subjectTimeSeries}
                          options={subjectTimeOptions}
                          height={280}
                        />
                      ) : (
                        <Box display="flex" alignItems="center" justifyContent="center" height={220}>
                          <Typography variant="caption" color="text.disabled">No timing data recorded</Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                </Grid>

                {/* 5c. Question-by-Question Time Distribution Chart */}
                <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Question-Wise Time Distribution
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Exact seconds spent on each individual question throughout the exam. Hover to view attempt details.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Box sx={{ width: 10, height: 10, bgcolor: 'success.main', borderRadius: '50%' }} />
                        <Typography variant="caption">Correct</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Box sx={{ width: 10, height: 10, bgcolor: 'error.main', borderRadius: '50%' }} />
                        <Typography variant="caption">Incorrect</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Box sx={{ width: 10, height: 10, bgcolor: 'text.disabled', borderRadius: '50%' }} />
                        <Typography variant="caption">Unattempted</Typography>
                      </Box>
                    </Stack>
                  </Box>

                  {allQuestionsFlattened.length > 0 ? (
                    <Chart
                      type="bar"
                      series={qTimeSeries}
                      options={qTimeOptions}
                      height={320}
                    />
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height={160}>
                      <Typography variant="caption" color="text.disabled">Question timing data unavailable</Typography>
                    </Box>
                  )}
                </Card>

                {/* 5d. Subject Pacing Breakdown Table */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Subject-Wise Pacing & Efficiency Breakdown
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2.5 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: (t) => t.palette.background.neutral || '#f4f6f8' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Total Time</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Avg / Question</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Time on Correct</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Time on Wrong</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Time on Skipped</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timeAnalysis.subject_time_analysis.map((st) => {
                        return (
                          <TableRow key={st.subject} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{st.subject}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>{formatTimeSpent(st.total_time)}</TableCell>
                            <TableCell align="center">{formatTimeSpent(st.avg_time)}</TableCell>
                            <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                              {formatTimeSpent(st.correct_time)} ({formatTimeSpent(st.avg_time_correct)}/q)
                            </TableCell>
                            <TableCell align="center" sx={{ color: 'error.main', fontWeight: 600 }}>
                              {formatTimeSpent(st.wrong_time)} ({formatTimeSpent(st.avg_time_wrong)}/q)
                            </TableCell>
                            <TableCell align="center" sx={{ color: 'text.secondary' }}>
                              {formatTimeSpent(st.unattempted_time)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* 5e. Pacing Highlights & Key Takeaways */}
                {(timeAnalysis.fastest_correct || timeAnalysis.slowest_question) && (
                  <Grid container spacing={2}>
                    {timeAnalysis.fastest_correct && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'success.light', bgcolor: 'success.lighter', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Iconify icon="solar:fire-bold" width={28} sx={{ color: 'success.main', flexShrink: 0 }} />
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.darker', display: 'block' }}>
                              Fastest Correct Question: Q{timeAnalysis.fastest_correct.qNo} ({timeAnalysis.fastest_correct.subject})
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 600 }}>
                              Solved in {formatTimeSpent(timeAnalysis.fastest_correct.time_spent)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {timeAnalysis.slowest_question && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'warning.light', bgcolor: 'warning.lighter', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Iconify icon="solar:hourglass-line-bold" width={28} sx={{ color: 'warning.main', flexShrink: 0 }} />
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.darker', display: 'block' }}>
                              Most Time Spent: Q{timeAnalysis.slowest_question.qNo} ({timeAnalysis.slowest_question.subject})
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                              Invested {formatTimeSpent(timeAnalysis.slowest_question.time_spent)} ({timeAnalysis.slowest_question.isCorrect ? 'Answered Correctly' : timeAnalysis.slowest_question.isUnattempted ? 'Left Unattempted' : 'Answered Incorrectly'})
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Card>

              {/* 6. Question-Wise Detailed Responses Table (from Tab 1) - Only shown if Answer Key is enabled */}
              {Boolean(reportData?.show_answer_key) && (
                <Card sx={{ p: 2.5 }}>
                  <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Question-Wise Detailed Responses
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    SA : Student Attempt | AK : Answer Key | QL : Ques. Difficulty Level
                  </Typography>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table
                    size="small"
                    sx={{
                      '& td, & th': {
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 0.5,
                        fontSize: '11px',
                        textAlign: 'center',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ bgcolor: (t) => t.palette.background.neutral || '#f4f6f8' }}>
                        {subjectsList.map((subj) => (
                          <TableCell
                            key={subj}
                            colSpan={5}
                            sx={{ fontWeight: 'bold', fontSize: '12px' }}
                          >
                            {subj}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow sx={{ bgcolor: (t) => t.palette.background.neutral || '#f4f6f8' }}>
                        {subjectsList.map((subj) => (
                          <React.Fragment key={`${subj}-headers`}>
                            <TableCell sx={{ fontWeight: 'bold', width: '25px' }}>QNo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '25px' }}>SA</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '25px' }}>AK</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '25px' }}>QL</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '38px' }}>Time</TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from({ length: maxQuestionsInSubject }).map((_, index) => (
                        <TableRow key={index} hover>
                          {subjectsList.map((subj) => {
                            const item = questionWiseData[subj]?.[index];
                            if (!item) {
                              return (
                                <React.Fragment key={`${subj}-${index}-empty`}>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                </React.Fragment>
                              );
                            }

                            const isIncorrect = !item.isCorrect;
                            const saColor = isIncorrect ? '#d32f2f' : '#1976d2';
                            const qlColor = isIncorrect ? '#d32f2f' : 'inherit';

                            return (
                              <React.Fragment key={`${subj}-${index}`}>
                                <TableCell sx={{ fontWeight: 'bold' }}>{item.qNo}</TableCell>
                                <TableCell
                                  sx={{
                                    color: saColor,
                                    fontWeight: isIncorrect ? 'bold' : 'normal',
                                  }}
                                >
                                  {Array.isArray(item.sa) ? (item.sa.length > 0 ? item.sa.join(', ') : '-') : item.sa}
                                </TableCell>
                                <TableCell>
                                  {Array.isArray(item.ak) ? (item.ak.length > 0 ? item.ak.join(', ') : '-') : item.ak}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: qlColor,
                                    fontWeight: isIncorrect ? 'bold' : 'normal',
                                  }}
                                >
                                  {item.ql}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    color: item.time_spent ? 'text.primary' : 'text.disabled',
                                  }}
                                >
                                  {formatTimeSpent(item.time_spent)}
                                </TableCell>
                              </React.Fragment>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={2}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#1976d2', borderRadius: '2px' }} />
                      <Typography variant="caption">Correct Attempt</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#d32f2f', borderRadius: '2px' }} />
                      <Typography variant="caption">Incorrect Attempt</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 12, height: 12, bgcolor: 'text.disabled', borderRadius: '2px' }} />
                      <Typography variant="caption">Unattempted</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Card>
              )}
            </Box>
          )}

          {/* ========================================================================= */}
          {/* TAB 2: STUDENT REPORT CARD (GRAPHICAL REPRESENTATION SHOWS LAST 10 QUIZZES) */}
          {/* ========================================================================= */}
          {activeTab === 'report_card' && (
            <Box>
              {/* Report Card Performance Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={2.4}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'primary.lighter',
                      borderColor: 'primary.light',
                    }}
                  >
                    <Typography variant="caption" color="primary.dark" fontWeight={600}>
                      Tests Recorded
                    </Typography>
                    <Typography
                      variant="h5"
                      color="primary.darker"
                      sx={{ fontWeight: 700, mt: 0.5 }}
                    >
                      {reportCard.tests_taken || reportCardTests.length || 1}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'success.lighter',
                      borderColor: 'success.light',
                    }}
                  >
                    <Typography variant="caption" color="success.dark" fontWeight={600}>
                      Marks Scored
                    </Typography>
                    <Typography
                      variant="h5"
                      color="success.darker"
                      sx={{ fontWeight: 700, mt: 0.5 }}
                    >
                      {formatMarks(testResult.scored_marks || totalSummary.marksObtained)}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'info.lighter',
                      borderColor: 'info.light',
                    }}
                  >
                    <Typography variant="caption" color="info.dark" fontWeight={600}>
                      Total Marks
                    </Typography>
                    <Typography
                      variant="h5"
                      color="info.darker"
                      sx={{ fontWeight: 700, mt: 0.5 }}
                    >
                      {formatMarks(testResult.total_marks )}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'warning.lighter',
                      borderColor: 'warning.light',
                    }}
                  >
                    <Typography variant="caption" color="warning.dark" fontWeight={600}>
                      Percentage Score
                    </Typography>
                    <Typography
                      variant="h5"
                      color="warning.darker"
                      sx={{ fontWeight: 700, mt: 0.5 }}
                    >
                      {testResult?.percentage || '-'}%
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'secondary.lighter',
                      borderColor: 'secondary.light',
                    }}
                  >
                    <Typography variant="caption" color="secondary.dark" fontWeight={600}>
                      Current Rank
                    </Typography>
                    <Typography
                      variant="h5"
                      color="secondary.darker"
                      sx={{ fontWeight: 700, mt: 0.5 }}
                    >
                      #{testResult.rank || '-'}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Graphical Representations for Report Card (SHOWING DETAILS FOR THE LAST 10 GIVEN QUIZZES) */}
              {chartTests.length > 0 && (
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={7}>
                    <Card sx={{ p: 2.5, height: '100%' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Examination Marks Comparison (Scored vs Total)
                        </Typography>
                        <Chip
                          size="small"
                          label={`Last ${chartTests.length} Quizzes`}
                          variant="soft"
                          color="primary"
                          sx={{ fontSize: '11px', height: 22 }}
                        />
                      </Box>
                      <Chart
                        dir="ltr"
                        type="bar"
                        series={testMarksSeries}
                        options={testMarksOptions}
                        height={270}
                      />
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Card sx={{ p: 2.5, height: '100%' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Class Rank Trajectory (Lower is Better)
                        </Typography>
                        <Chip
                          size="small"
                          label={`Last ${chartTests.length} Quizzes`}
                          variant="soft"
                          color="warning"
                          sx={{ fontSize: '11px', height: 22 }}
                        />
                      </Box>
                      <Chart
                        dir="ltr"
                        type="line"
                        series={rankSeries}
                        options={rankOptions}
                        height={270}
                      />
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card sx={{ p: 2.5 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Test Percentage Score Performance Trend
                        </Typography>
                        <Chip
                          size="small"
                          label={`Last ${chartTests.length} Quizzes`}
                          variant="soft"
                          color="info"
                          sx={{ fontSize: '11px', height: 22 }}
                        />
                      </Box>
                      <Chart
                        dir="ltr"
                        type="line"
                        series={trendSeries}
                        options={trendOptions}
                        height={270}
                      />
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Comprehensive Tests History Table */}
              <Card sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Examination Performance History
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead
                      sx={{ bgcolor: (t) => t.palette.background.neutral || '#f4f6f8' }}
                    >
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Examination Name</TableCell>
                  
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Date
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Marks Scored
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Total Marks
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Percentage
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Rank
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(reportCardTests.length > 0 ? reportCardTests : [
                        {
                          s_no: 1,
                          quiz_name: testName,
                          mode: testType,
                          date: 'Recent',
                          scored_marks: formatMarks(totalSummary.marksObtained),
                          total_marks: formatMarks(totalSummary.totalQues * 4),
                          percentage: accuracyPct,
                          rank: 1,
                        },
                      ]).map((t, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{t.s_no || idx + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{t.quiz_name}</TableCell>
                
              
                          <TableCell align="center">{t.date || '-'}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {formatMarks(t.scored_marks)}
                          </TableCell>
                          <TableCell align="center">{formatMarks(t.total_marks)}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>
                            {t.percentage}%
                          </TableCell>
                          <TableCell align="center">#{t.rank || 1}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Box>
          )}
        </>
      )}

      {/* ========================================================================= */}
      {/* FULL-SCREEN EXAM PREVIEW DRAWER (WITH QUESTION NAVIGATOR & ATTEMPTS)     */}
      {/* ========================================================================= */}
      <Drawer
        anchor="right"
        open={examPreviewOpen}
        onClose={() => setExamPreviewOpen(false)}
        PaperProps={{
          sx: {
            width: '100vw',
            maxWidth: '100vw',
            height: '100vh',
            bgcolor: '#F8F9FA',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: (t) => t.zIndex.modal + 15,
          },
        }}
      >
        {/* 1. Top Navigation Bar (Sticky) */}
        <Box
          sx={{
            p: { xs: 1.5, md: 2 },
            px: { xs: 2, md: 3 },
            bgcolor: '#FFFFFF',
            borderBottom: '1px solid #E8E7E3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            flexShrink: 0,
          }}
        >
          {/* Left: Back button & Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setExamPreviewOpen(false)}
              startIcon={<Iconify icon="solar:arrow-left-bold" width={18} />}
              sx={{
                borderColor: '#236B59',
                color: '#236B59',
                fontWeight: 700,
                borderRadius: '10px',
                textTransform: 'none',
                px: 1.5,
                py: 0.6,
                '&:hover': { bgcolor: '#EFF7F4' },
              }}
            >
              Back to Report
            </Button>
            
          </Box>

          {/* Right: Student Info & Close */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {reportData?.student_info?.name && (
              <Chip
                label={reportData.student_info.name}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 700, fontSize: '0.8rem', height: 32 }}
              />
            )}
            <IconButton
              onClick={() => setExamPreviewOpen(false)}
              sx={{ bgcolor: '#F3F4F6', '&:hover': { bgcolor: '#E5E7EB' } }}
              size="small"
            >
              <Iconify icon="solar:close-circle-bold" width={22} />
            </IconButton>
          </Box>
        </Box>

        {/* 2. Sub-Header: Section Filter Tabs & Comprehensive Status Legend */}
        <Box
          sx={{
            bgcolor: '#FAFAF9',
            borderBottom: '1px solid #E8E7E3',
            p: { xs: 1.5, md: 2 },
            px: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          {/* Section Selector Tabs */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              maxWidth: '100%',
              pb: { xs: 0.5, md: 0 },
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Chip
              label={`All Sections (${(examPreviewQuizData || []).reduce((acc, s) => acc + (s.questions?.length || 0), 0)})`}
              onClick={() => setExamPreviewSectionTab('all')}
              color={examPreviewSectionTab === 'all' ? 'primary' : 'default'}
              variant={examPreviewSectionTab === 'all' ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '0.82rem',
                bgcolor: examPreviewSectionTab === 'all' ? '#236B59' : '#FFFFFF',
                '&.MuiChip-filled': { color: '#FFFFFF' },
              }}
            />
            {(examPreviewQuizData || []).map((sec, sIdx) => {
              const sTotal = sec.questions?.length || 0;
              const sName = sec.subject_name || sec.name || `Section ${sIdx + 1}`;
              const isCurrent = examPreviewSectionTab === sIdx;
              return (
                <Chip
                  key={sIdx}
                  label={`${sName} (${sTotal})`}
                  onClick={() => setExamPreviewSectionTab(sIdx)}
                  color={isCurrent ? 'primary' : 'default'}
                  variant={isCurrent ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    bgcolor: isCurrent ? '#236B59' : '#FFFFFF',
                    '&.MuiChip-filled': { color: '#FFFFFF' },
                  }}
                />
              );
            })}
          </Box>

          {/* Status Legend & Mobile Navigator Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: '#2e7d32' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151' }}>
                Correct
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: '#d32f2f' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151' }}>
                Incorrect
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: '#9E9E9E' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151' }}>
                Unattempted
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={() => setExamPreviewMobileNavOpen(true)}
              startIcon={<Iconify icon="solar:menu-dots-bold" width={16} />}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                borderColor: '#236B59',
                color: '#236B59',
                fontWeight: 700,
                borderRadius: '10px',
                textTransform: 'none',
                fontSize: '0.78rem',
                px: 1.5,
                py: 0.4,
                bgcolor: '#FFFFFF',
                '&:hover': { bgcolor: '#EFF7F4' },
              }}
            >
              Navigator
            </Button>
          </Box>
        </Box>

        {/* 3. Main Content: Questions List & Sticky Question Navigator */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 3 }, bgcolor: '#F8F9FA' }}>
          {loadingExamPreview ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 350, gap: 2 }}>
              <CircularProgress sx={{ color: '#236B59' }} />
              <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600 }}>
                Loading examination questions and diagrams...
              </Typography>
            </Box>
          ) : examPreviewError ? (
            <Box sx={{ maxWidth: 600, mx: 'auto', py: 6, textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {examPreviewError}
              </Alert>
              <Button variant="outlined" onClick={handleOpenExamPreview} sx={{ borderRadius: '8px' }}>
                Retry Loading Exam
              </Button>
            </Box>
          ) : !examPreviewQuizData || examPreviewQuizData.length === 0 ? (
            <Box sx={{ maxWidth: 600, mx: 'auto', py: 6, textAlign: 'center' }}>
              <Alert severity="info">No questions available for this examination.</Alert>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 3, maxWidth: 1440, mx: 'auto', alignItems: 'flex-start' }}>
              {/* Questions List (Left Column) */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {examPreviewQuizData.map((sec, sIdx) => {
                  if (examPreviewSectionTab !== 'all' && examPreviewSectionTab !== sIdx) {
                    return null;
                  }
                  const secQuestions = sec.questions || [];

                  return (
                    <Box key={sIdx} sx={{ mb: 4 }}>
                      {/* Section Header Card */}
                      <Box
                        sx={{
                          mb: 2.5,
                          p: 2,
                          px: 2.5,
                          borderRadius: '14px',
                          bgcolor: '#EBF4F1',
                          border: '1.5px solid #C4E2D8',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1B5244' }}>
                            {sec.subject_name || sec.name || `Section ${sIdx + 1}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#4A6B61', fontWeight: 600 }}>
                            Correct Marks: +{sec.correctMarksWeightage || 1.0} | Negative: -{sec.negativeMarkingWeightage || 0}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1B5244' }}>
                          {secQuestions.length} Questions
                        </Typography>
                      </Box>

                      {/* Question Cards */}
                      {secQuestions.map((question, qIdx) => {
                        const globalQNo =
                          examPreviewQuizData
                            .slice(0, sIdx)
                            .reduce((acc, s) => acc + (s.questions?.length || 0), 0) + qIdx + 1;

                        const qDetail =
                          allQuestionsFlattened.find((q) => String(q.question_id || q.questionId) === String(question.questionId || question.id)) ||
                          allQuestionsFlattened.find((q) => String(q.qNo) === String(globalQNo)) ||
                          allQuestionsFlattened[globalQNo - 1];

                        const studentAns = qDetail?.sa;
                        const isCorrect = qDetail?.isCorrect;
                        const marksAwarded = qDetail?.marks_awarded;
                        const timeSpent = qDetail?.time_spent;
                        const hasAttempt =
                          studentAns !== undefined &&
                          studentAns !== null &&
                          studentAns !== '' &&
                          studentAns !== '-' &&
                          studentAns !== 'Not Attempted' &&
                          !(Array.isArray(studentAns) && studentAns.length === 0);

                        const correctAnswersList = Array.isArray(question.correctAnswers)
                          ? question.correctAnswers.map(String)
                          : [];

                        let displayCorrectKey =
                          qDetail?.ak && qDetail.ak !== '-' && qDetail.ak !== 'N/A'
                            ? qDetail.ak
                            : correctAnswersList.length > 0
                            ? correctAnswersList.join(', ')
                            : null;

                        if (!displayCorrectKey && (question.questionType === 'MCQ' || question.questionType === 'MSQ')) {
                          const correctIdx = (question.answers || []).findIndex((a) => a.is_correct);
                          if (correctIdx !== -1) {
                            displayCorrectKey = String(correctIdx + 1);
                          }
                        }

                        let cardBorderColor = '#E5E7EB';
                        if (hasAttempt) {
                          cardBorderColor = isCorrect ? '#2E7D32' : '#D32F2F';
                        }

                        return (
                          <Card
                            key={qIdx}
                            id={`exam-preview-question-${sIdx}-${qIdx}`}
                            variant="outlined"
                            sx={{
                              mb: 3,
                              borderRadius: '16px',
                              border: `1.5px solid ${cardBorderColor}`,
                              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                              overflow: 'hidden',
                              bgcolor: '#FFFFFF',
                            }}
                          >
                            {/* Card Top Bar */}
                            <Box
                              sx={{
                                p: 2,
                                px: { xs: 2, md: 3 },
                                bgcolor: hasAttempt
                                  ? isCorrect
                                    ? '#F0F9F4'
                                    : '#FEF2F2'
                                  : '#F9FAFB',
                                borderBottom: '1px solid #ECECEC',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1F2421' }}>
                                  Question {globalQNo}
                                </Typography>
                                <Chip
                                  label={question.questionType || 'MCQ'}
                                  size="small"
                                  sx={{ fontWeight: 700, fontSize: '0.72rem', height: 22 }}
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                  ({sec.name || 'Section'} — Q{qIdx + 1} of {secQuestions.length})
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {hasAttempt ? (
                                  isCorrect ? (
                                    <Chip
                                      label="Correct"
                                      size="small"
                                      sx={{ bgcolor: '#2E7D32', color: '#fff', fontWeight: 700 }}
                                    />
                                  ) : (
                                    <Chip
                                      label="Incorrect"
                                      size="small"
                                      sx={{ bgcolor: '#D32F2F', color: '#fff', fontWeight: 700 }}
                                    />
                                  )
                                ) : (
                                  <Chip
                                    label="Unattempted"
                                    size="small"
                                    sx={{ bgcolor: '#F3F4F6', color: '#4B5563', border: '1px solid #D1D5DB', fontWeight: 700 }}
                                  />
                                )}
                              </Box>
                            </Box>

                            {/* Question Content & Images */}
                            <Box sx={{ p: { xs: 2, md: 3 } }}>
                              <Box
                                dangerouslySetInnerHTML={{ __html: question.questionContent }}
                                  sx={{
                                mb: 2.5,
                                fontSize: { xs: '0.95rem', md: '1rem' },
                                lineHeight: 1.6,
                                color: '#2D3748',
                                '& img': {
                                    maxWidth: { xs: '100%', lg: '55%' },
                                    maxHeight: { xs: 340, sm: 550, md: 600, lg: 350 },
                                    width: { xs: 'auto', sm: '75%', md: '70%', lg: 'auto' },
                                    minWidth: { xs: 'unset', sm: '55%', md: '50%', lg: 'unset' },
                                    height: 'auto',
                                    display: 'block',
                                    mx: { xs: 'auto', sm: 'auto', lg: '0' },
                                    my: 2,
                                    borderRadius: 1.5,
                                    objectFit: 'contain',
                                },
                                '& p': { mb: 1 },
                                overflowX: 'auto',
                            }}
                              />

                              {/* MCQ & MSQ Options Display */}
                              {(question.questionType === 'MCQ' || question.questionType === 'MSQ') && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                                  {(question.answers || []).map((answer, aIdx) => {
                                    const optionNum = String(aIdx + 1);
                                    const optionId = String(answer.id);
                                    const isStudentAnswer =
                                      hasAttempt &&
                                      (String(studentAns) === optionNum ||
                                        String(studentAns) === optionId ||
                                        (Array.isArray(studentAns) &&
                                          (studentAns.includes(optionNum) || studentAns.includes(optionId))));

                                    const isOptionCorrectAnswer =
                                      Boolean(answer.is_correct) ||
                                      correctAnswersList.includes(optionNum) ||
                                      correctAnswersList.includes(optionId) ||
                                      (displayCorrectKey &&
                                        displayCorrectKey !== '-' &&
                                        displayCorrectKey !== 'N/A' &&
                                        (String(displayCorrectKey) === optionNum ||
                                          String(displayCorrectKey) === optionId ||
                                          String(displayCorrectKey).split(',').map((s) => s.trim()).includes(optionNum) ||
                                          String(displayCorrectKey).split(',').map((s) => s.trim()).includes(optionId)));

                                    let optBorder = '1px solid #E5E7EB';
                                    let optBg = '#FFFFFF';
                                    let badgeColor = null;
                                    let badgeText = null;

                                    if (isStudentAnswer) {
                                      if (isCorrect) {
                                        optBorder = '2px solid #2E7D32';
                                        optBg = '#EFF7F4';
                                        badgeColor = '#2E7D32';
                                        badgeText = 'Your Answer (Correct)';
                                      } else {
                                        optBorder = '2px solid #D32F2F';
                                        optBg = '#FEF2F2';
                                        badgeColor = '#D32F2F';
                                        badgeText = 'Your Answer (Incorrect)';
                                      }
                                    } else if (isOptionCorrectAnswer) {
                                      optBorder = '2px dashed #2E7D32';
                                      optBg = '#F0FDF4';
                                      badgeColor = '#2E7D32';
                                      badgeText = 'Correct Answer';
                                    }

                                    const optionLabel = String.fromCharCode(65 + aIdx);

                                    return (
                                      <Box
                                        key={answer.id || aIdx}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          p: 1.5,
                                          px: 2,
                                          borderRadius: '12px',
                                          border: optBorder,
                                          bgcolor: optBg,
                                          cursor: 'default',
                                          position: 'relative',
                                          '& img': {
                                            maxWidth: '100%',
                                            maxHeight: 80,
                                            objectFit: 'contain',
                                            display: 'inline-block',
                                            verticalAlign: 'middle',
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 26,
                                            height: 26,
                                            borderRadius: '50%',
                                            bgcolor: isStudentAnswer
                                              ? (isCorrect ? '#2E7D32' : '#D32F2F')
                                              : (isOptionCorrectAnswer ? '#2E7D32' : '#F3F4F6'),
                                            color: (isStudentAnswer || isOptionCorrectAnswer) ? '#FFFFFF' : '#4B5563',
                                            fontWeight: 700,
                                            fontSize: '0.82rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1.5,
                                            flexShrink: 0,
                                          }}
                                        >
                                          {optionLabel}
                                        </Box>

                                        <Box sx={{ flex: 1, fontSize: '0.95rem', fontWeight: (isStudentAnswer || isOptionCorrectAnswer) ? 600 : 500, color: '#1F2421' }}>
                                          <div dangerouslySetInnerHTML={{ __html: answer.item }} />
                                        </Box>

                                        {badgeText && (
                                          <Chip
                                            label={badgeText}
                                            size="small"
                                            sx={{
                                              bgcolor: badgeColor,
                                              color: '#FFFFFF',
                                              fontWeight: 700,
                                              fontSize: '0.72rem',
                                              height: 22,
                                              ml: 1,
                                            }}
                                          />
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}

                              {/* Integer / Decimal Answer Display */}
                              {(question.questionType === 'Integer' || question.questionType === 'Decimal') && (
                                <Box sx={{ maxWidth: 450, mb: 2 }}>
                                  <Box sx={{ p: 1.5, bgcolor: hasAttempt ? (isCorrect ? '#EFF7F4' : '#FEF2F2') : '#F9FAFB', border: hasAttempt ? (isCorrect ? '1px solid #A7F3D0' : '1px solid #FECACA') : '1px solid #E5E7EB', borderRadius: '10px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#4B5563' }}>
                                      Student Response: <span style={{ fontWeight: 800, color: hasAttempt ? (isCorrect ? '#2E7D32' : '#D32F2F') : '#D93025' }}>{hasAttempt ? studentAns : 'Unattempted'}</span>
                                    </Typography>
                                    {displayCorrectKey && (
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2E7D32', mt: 0.5 }}>
                                        Correct Answer: <span style={{ fontWeight: 800 }}>{displayCorrectKey}</span>
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              )}

                              {/* Matching / MAT Matrix Answer Display */}
                              {(question.questionType === 'Matching' || question.questionType === 'MAT') && (
                                <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#1F2421' }}>
                                    Match the following
                                  </Typography>
                                  <Box sx={{
                                    display: 'flex',
                                    border: '1.5px solid #333',
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                    width: '100%',
                                    maxWidth: { xs: '100%', sm: 380, md: 440 },
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    bgcolor: '#FFFFFF'
                                  }}>
                                    {/* Column A */}
                                    <Box sx={{ borderRight: '1.5px solid #333', width: { xs: 44, sm: 54 }, flexShrink: 0, bgcolor: '#F5F5F5' }}>
                                      <Box sx={{ height: { xs: 34, sm: 40 }, borderBottom: '1.5px solid #333' }} />
                                      {(question.answers || []).filter((ans) => ans.column === 'A').map((row, index) => (
                                        <Box key={row.id || index} sx={{
                                          height: { xs: 38, sm: 46 },
                                          borderBottom: index < (question.answers || []).filter((ans) => ans.column === 'A').length - 1 ? '1px solid #ddd' : 'none',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}>
                                          <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                                            {columnALabels[index] || String.fromCharCode(97 + index)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>

                                    {/* Column B */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', height: { xs: 34, sm: 40 }, borderBottom: '1.5px solid #333', bgcolor: '#F5F5F5' }}>
                                        {(question.answers || []).filter((ans) => ans.column === 'B').map((col, index) => (
                                          <Box key={col.id || index} sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            borderRight: index < (question.answers || []).filter((ans) => ans.column === 'B').length - 1 ? '1px solid #ddd' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                                              {columnBLabels[index] || ['p', 'q', 'r', 's'][index] || String(index + 1)}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>

                                      {(question.answers || []).filter((ans) => ans.column === 'A').map((row, rowIndex) => (
                                        <Box key={row.id || rowIndex} sx={{
                                          display: 'flex',
                                          height: { xs: 38, sm: 46 },
                                          borderBottom: rowIndex < (question.answers || []).filter((ans) => ans.column === 'A').length - 1 ? '1px solid #ddd' : 'none'
                                        }}>
                                          {(question.answers || []).filter((ans) => ans.column === 'B').map((col, colIndex) => {
                                            const cellKey1 = `${row.id}_${col.id}`;
                                            const cellKey2 = `${rowIndex + 1}_${colIndex + 5}`;
                                            const cellKey3 = `${rowIndex + 1}_${colIndex + 1}`;
                                            const isSelected = hasAttempt && (() => {
                                              const selectedList = Array.isArray(studentAns)
                                                ? studentAns.map(String)
                                                : (typeof studentAns === 'string'
                                                    ? (studentAns.startsWith('[')
                                                        ? (() => { try { return JSON.parse(studentAns).map(String); } catch(e) { return studentAns.split(',').map(s => s.trim()); } })()
                                                        : studentAns.split(',').map((s) => s.trim()))
                                                    : []);
                                              return selectedList.some((item) => {
                                                const cleanItem = String(item).trim();
                                                if (cleanItem === cellKey1 || cleanItem === cellKey2 || cleanItem === cellKey3) return true;
                                                let rP = '', cP = '';
                                                if (cleanItem.includes('→')) [rP, cP] = cleanItem.split('→');
                                                else if (cleanItem.includes('->')) [rP, cP] = cleanItem.split('->');
                                                else if (cleanItem.includes('_')) [rP, cP] = cleanItem.split('_');
                                                else if (cleanItem.includes('-')) [rP, cP] = cleanItem.split('-');
                                                if (rP && cP) {
                                                  rP = rP.trim();
                                                  cP = cP.trim();
                                                  const rMatches = (rP === String(row.id) || rP === String(rowIndex + 1) || rP.toLowerCase() === (columnALabels[rowIndex] || '').toLowerCase());
                                                  const cMatches = (cP === String(col.id) || cP === String(colIndex + 5) || cP === String(colIndex + 1) || cP.toLowerCase() === (columnBLabels[colIndex] || '').toLowerCase());
                                                  if (rMatches && cMatches) return true;
                                                }
                                                return false;
                                              });
                                            })();

                                            const isCorrectMatch = Boolean(
                                              (Array.isArray(row.correctMatches) && (
                                                row.correctMatches.map(String).includes(String(col.id)) ||
                                                row.correctMatches.map(String).includes(String(colIndex + 5)) ||
                                                row.correctMatches.map(String).includes(String(colIndex + 1)) ||
                                                row.correctMatches.map(String).map(s => s.toLowerCase()).includes((columnBLabels[colIndex] || '').toLowerCase())
                                              )) ||
                                              (Array.isArray(qDetail?.ak) && qDetail.ak.some((akItem) => {
                                                const cleanAk = String(akItem).trim();
                                                if (cleanAk === cellKey1 || cleanAk === cellKey2 || cleanAk === cellKey3) return true;
                                                let rP = '', cP = '';
                                                if (cleanAk.includes('→')) [rP, cP] = cleanAk.split('→');
                                                else if (cleanAk.includes('->')) [rP, cP] = cleanAk.split('->');
                                                else if (cleanAk.includes('_')) [rP, cP] = cleanAk.split('_');
                                                else if (cleanAk.includes('-')) [rP, cP] = cleanAk.split('-');
                                                if (rP && cP) {
                                                  rP = rP.trim();
                                                  cP = cP.trim();
                                                  const rMatches = (rP === String(row.id) || rP === String(rowIndex + 1) || rP.toLowerCase() === (columnALabels[rowIndex] || '').toLowerCase());
                                                  const cMatches = (cP === String(col.id) || cP === String(colIndex + 5) || cP === String(colIndex + 1) || cP.toLowerCase() === (columnBLabels[colIndex] || '').toLowerCase());
                                                  if (rMatches && cMatches) return true;
                                                }
                                                return false;
                                              }))
                                            );
                                            let circleBorder = '2px solid #555';
                                            let circleBg = 'transparent';

                                            if (isSelected) {
                                              if (isCorrectMatch) {
                                                circleBorder = '2px solid #2E7D32';
                                                circleBg = '#2E7D32';
                                              } else {
                                                circleBorder = '2px solid #D32F2F';
                                                circleBg = '#D32F2F';
                                              }
                                            } else if (isCorrectMatch) {
                                              circleBorder = '2px dashed #2E7D32';
                                              circleBg = '#A7F3D0';
                                            }

                                            return (
                                              <Box key={col.id || colIndex} sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                borderRight: colIndex < (question.answers || []).filter((ans) => ans.column === 'B').length - 1 ? '1px solid #ddd' : 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}>
                                                <Box
                                                  sx={{
                                                    width: { xs: 16, sm: 18 },
                                                    height: { xs: 16, sm: 18 },
                                                    borderRadius: '50%',
                                                    border: circleBorder,
                                                    backgroundColor: circleBg,
                                                    cursor: 'default'
                                                  }}
                                                />
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                  {/* Legend for Matrix Circles */}
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: { xs: 1.5, sm: 2.5 },
                                    mt: 1.5,
                                    p: 1,
                                    bgcolor: '#F9FAFB',
                                    borderRadius: 1.5,
                                    border: '1px solid #E5E7EB',
                                    width: 'fit-content',
                                    maxWidth: '100%',
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#2E7D32', border: '2px solid #2E7D32' }} />
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1F2421' }}>
                                        Attempted (Correct)
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#D32F2F', border: '2px solid #D32F2F' }} />
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1F2421' }}>
                                        Attempted (Incorrect)
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#A7F3D0', border: '2px dashed #2E7D32' }} />
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1F2421' }}>
                                        Correct Match (Missed)
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'transparent', border: '2px solid #555' }} />
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280' }}>
                                        Unselected
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              )}

                              {/* Question Summary Metadata Bar */}
                              <Box
                                sx={{
                                  mt: 2,
                                  pt: 1.5,
                                  borderTop: '1px dashed #E5E7EB',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  gap: 1.5,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: hasAttempt ? (isCorrect ? '#2E7D32' : '#D32F2F') : '#D93025' }}>
                                    Your Response:{' '}
                                    <span style={{ fontWeight: 800 }}>
                                      {hasAttempt
                                        ? (question.questionType === 'MCQ' || question.questionType === 'MSQ'
                                            ? `Option ${studentAns}${isCorrect ? ' (Correct)' : ' (Incorrect)'}`
: (question.questionType === 'Matching' || question.questionType === 'MAT')
                                            ? (() => {
                                                const rawList = Array.isArray(studentAns)
                                                  ? studentAns
                                                  : (typeof studentAns === 'string'
                                                      ? (studentAns.startsWith('[')
                                                          ? (() => { try { return JSON.parse(studentAns); } catch(e) { return studentAns.split(',').map(s => s.trim()); } })()
                                                          : studentAns.split(',').map(s => s.trim()))
                                                      : []);
                                                const validItems = rawList.map(String).map(s => s.trim()).filter(s => Boolean(s) && s !== '-' && s !== 'Not Attempted');
                                                if (validItems.length > 0) {
                                                  const aRows = (question.answers || []).filter(a => a.column === 'A');
                                                  const bCols = (question.answers || []).filter(a => a.column === 'B');
                                                  return validItems.map(pair => {
                                                    let rP = '', cP = '';
                                                    if (pair.includes('→')) [rP, cP] = pair.split('→');
                                                    else if (pair.includes('->')) [rP, cP] = pair.split('->');
                                                    else if (pair.includes('_')) [rP, cP] = pair.split('_');
                                                    else if (pair.includes('-')) [rP, cP] = pair.split('-');
                                                    else return pair;

                                                    rP = rP.trim();
                                                    cP = cP.trim();
                                                    let rIdx = aRows.findIndex(a => String(a.id) === rP);
                                                    if (rIdx === -1 && Number(rP) >= 1 && Number(rP) <= aRows.length) {
                                                      rIdx = Number(rP) - 1;
                                                    }
                                                    let cIdx = bCols.findIndex(a => String(a.id) === cP);
                                                    if (cIdx === -1 && Number(cP) >= 1 && Number(cP) <= bCols.length) {
                                                      cIdx = Number(cP) - 1;
                                                    } else if (cIdx === -1 && Number(cP) >= 5 && Number(cP) <= 4 + bCols.length) {
                                                      cIdx = Number(cP) - 5;
                                                    }
                                                    const rLbl = rIdx !== -1 && columnALabels[rIdx] ? columnALabels[rIdx] : (columnALabels[Number(rP)-1] || rP);
                                                    const cLbl = cIdx !== -1 && columnBLabels[cIdx] ? columnBLabels[cIdx] : (columnBLabels[Number(cP)-5] || cP);
                                                    return `${rLbl}→${cLbl}`;
                                                  }).join(', ');
                                                }
                                                return typeof studentAns === 'string' && studentAns !== '-' && studentAns !== '1' ? studentAns : 'Unattempted';
                                              })()
                                            : studentAns)
                                        : 'Unattempted'}
                                    </span>
                                  </Typography>
                                  {displayCorrectKey && (
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                                      Correct Answer:{' '}
                                      <span style={{ fontWeight: 800 }}>
                                        {question.questionType === 'MCQ' || question.questionType === 'MSQ'
                                          ? (String(displayCorrectKey).match(/^\d+$/)
                                              ? `Option ${displayCorrectKey} (${String.fromCharCode(64 + Number(displayCorrectKey))})`
                                              : displayCorrectKey)
                                          : (question.questionType === 'Matching' || question.questionType === 'MAT')
                                          ? (() => {
                                              const aRows = (question.answers || []).filter(a => a.column === 'A');
                                              const bCols = (question.answers || []).filter(a => a.column === 'B');
                                              const matches = [];
                                              aRows.forEach((r, rIdx) => {
                                                (r.correctMatches || []).forEach(cId => {
                                                  const cIdx = bCols.findIndex(c => String(c.id) === String(cId));
                                                  const rLbl = columnALabels[rIdx] || String.fromCharCode(97 + rIdx);
                                                  const cLbl = cIdx !== -1 && columnBLabels[cIdx] ? columnBLabels[cIdx] : cId;
                                                  matches.push(`${rLbl}→${cLbl}`);
                                                });
                                              });
                                              return matches.length > 0 ? matches.join(', ') : displayCorrectKey;
                                            })()
                                          : displayCorrectKey}
                                      </span>
                                    </Typography>
                                  )}
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  {timeSpent !== null && timeSpent !== undefined && (
                                    <Chip
                                      label={`Time: ${formatTimeSpent(timeSpent)}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }}
                                    />
                                  )}
                                  {marksAwarded !== null && marksAwarded !== undefined && (
                                    <Chip
                                      label={`${marksAwarded > 0 ? '+' : ''}${marksAwarded} Marks`}
                                      size="small"
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        height: 24,
                                        bgcolor: marksAwarded > 0 ? '#E6F4EA' : marksAwarded < 0 ? '#FCE8E6' : '#F1F3F4',
                                        color: marksAwarded > 0 ? '#137333' : marksAwarded < 0 ? '#C5221F' : '#5F6368',
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Card>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>

              {/* Sticky Desktop Question Navigator (Right Column) */}
              <Card
                variant="outlined"
                sx={{
                  width: { md: 310, lg: 340 },
                  display: { xs: 'none', md: 'block' },
                  position: 'sticky',
                  top: 0,
                  borderRadius: '16px',
                  p: 2,
                  bgcolor: '#FFFFFF',
                  border: '1.5px solid #E5E7EB',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.04)',
                  flexShrink: 0,
                  maxHeight: 'calc(100vh - 180px)',
                  overflowY: 'auto',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, pb: 1, borderBottom: '1px solid #ECECEC' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1F2421', fontSize: '0.95rem' }}>
                    Question Navigator
                  </Typography>
                  <Chip
                    label={`${(examPreviewQuizData || []).reduce((acc, s) => acc + (s.questions?.length || 0), 0)} Ques`}
                    size="small"
                    sx={{ bgcolor: '#E6F4EA', color: '#137333', fontWeight: 700, fontSize: '0.72rem', height: 22 }}
                  />
                </Box>

                {/* Section Switcher inside Navigator */}
                <Box sx={{ display: 'flex', gap: 0.75, mb: 2, overflowX: 'auto', pb: 0.5 }}>
                  <Chip
                    label="All"
                    size="small"
                    onClick={() => setExamPreviewNavSectionTab('all')}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      bgcolor: examPreviewNavSectionTab === 'all' ? '#236B59' : '#F3F4F6',
                      color: examPreviewNavSectionTab === 'all' ? '#FFFFFF' : '#374151',
                      '&:hover': { bgcolor: examPreviewNavSectionTab === 'all' ? '#1B5244' : '#E5E7EB' },
                    }}
                  />
                  {(examPreviewQuizData || []).map((sec, sIdx) => {
                    const sName = (sec.subject_name || sec.name || `S${sIdx + 1}`).slice(0, 8);
                    const isSel = examPreviewNavSectionTab === sIdx;
                    return (
                      <Chip
                        key={sIdx}
                        label={sName}
                        size="small"
                        onClick={() => setExamPreviewNavSectionTab(sIdx)}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          bgcolor: isSel ? '#236B59' : '#F3F4F6',
                          color: isSel ? '#FFFFFF' : '#374151',
                          '&:hover': { bgcolor: isSel ? '#1B5244' : '#E5E7EB' },
                        }}
                      />
                    );
                  })}
                </Box>

                {/* Question Palette Grid */}
                {(examPreviewQuizData || []).map((sec, sIdx) => {
                  if (examPreviewNavSectionTab !== 'all' && examPreviewNavSectionTab !== sIdx) return null;
                  const secQuestions = sec.questions || [];
                  const secStartNo = examPreviewQuizData.slice(0, sIdx).reduce((acc, s) => acc + (s.questions?.length || 0), 0);

                  return (
                    <Box key={sIdx} sx={{ mb: 2 }}>
                      {examPreviewQuizData.length > 1 && examPreviewNavSectionTab === 'all' && (
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#6B7280', display: 'block', mb: 0.75 }}>
                          {sec.subject_name || sec.name || `Section ${sIdx + 1}`}
                        </Typography>
                      )}
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.8 }}>
                        {secQuestions.map((_, qIdx) => {
                          const qNo = secStartNo + qIdx + 1;
                          const qDetail =
                            allQuestionsFlattened.find((q) => String(q.question_id || q.questionId) === String(sec?.questions?.[qIdx]?.questionId || sec?.questions?.[qIdx]?.id)) ||
                            allQuestionsFlattened.find((q) => String(q.qNo) === String(qNo)) ||
                            allQuestionsFlattened[qNo - 1];

                          const studentAns = qDetail?.sa;
                          const isCorrect = qDetail?.isCorrect;
                          const hasAttempt =
                            studentAns !== undefined &&
                            studentAns !== null &&
                            studentAns !== '' &&
                            studentAns !== '-' &&
                            studentAns !== 'Not Attempted';

                          let bg = '#F9FAFB';
                          let fg = '#6B7280';
                          let border = '1px solid #E5E7EB';

                          if (hasAttempt) {
                            if (isCorrect) {
                              bg = '#2E7D32';
                              fg = '#FFFFFF';
                              border = '1px solid #2E7D32';
                            } else {
                              bg = '#D32F2F';
                              fg = '#FFFFFF';
                              border = '1px solid #D32F2F';
                            }
                          }

                          return (
                            <Tooltip
                              key={qIdx}
                              title={`Q${qNo} — ${hasAttempt ? (isCorrect ? 'Correct' : 'Incorrect') : 'Unattempted'}`}
                            >
                              <Box
                                onClick={() => handleJumpToExamPreviewQuestion(sIdx, qIdx)}
                                sx={{
                                  width: '100%',
                                  height: 38,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '8px',
                                  bgcolor: bg,
                                  color: fg,
                                  border: border,
                                  fontWeight: 700,
                                  fontSize: '0.82rem',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                  '&:hover': {
                                    transform: 'scale(1.08)',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                  },
                                }}
                              >
                                {qNo}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Card>
            </Box>
          )}
        </Box>

        {/* 4. Drawer Footer (Sticky) */}
        <Box
          sx={{
            p: 2,
            px: { xs: 2, md: 3 },
            bgcolor: '#FFFFFF',
            borderTop: '1px solid #E8E7E3',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#4B5563' }}>
            Total Questions: {(examPreviewQuizData || []).reduce((acc, s) => acc + (s.questions?.length || 0), 0)} · Correct: <span style={{ color: '#137333', fontWeight: 700 }}>{reportData?.total_summary?.correct_count ?? '-'}</span> · Incorrect: <span style={{ color: '#D93025', fontWeight: 700 }}>{reportData?.total_summary?.wrong_count ?? '-'}</span> · Unattempted: <span style={{ color: '#5F6368', fontWeight: 700 }}>{reportData?.total_summary?.unattempted_count ?? '-'}</span>
          </Typography>

          <Button
            variant="outlined"
            onClick={() => setExamPreviewOpen(false)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              borderColor: '#236B59',
              color: '#236B59',
              px: 2.5,
              '&:hover': { bgcolor: '#EFF7F4' },
            }}
          >
            Back to Report
          </Button>
        </Box>
      </Drawer>

      {/* Mobile Navigator Drawer for Exam Preview */}
      <Drawer
        anchor="right"
        open={examPreviewMobileNavOpen}
        onClose={() => setExamPreviewMobileNavOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '85%', sm: 360 },
            p: 2.5,
            zIndex: (t) => t.zIndex.modal + 25,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '1px solid #E0E0E0' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1F2421', fontSize: '1rem' }}>
            Question Navigator
          </Typography>
          <IconButton size="small" onClick={() => setExamPreviewMobileNavOpen(false)}>
            <Iconify icon="solar:close-circle-bold" width={20} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75, mb: 2, overflowX: 'auto', pb: 0.5 }}>
          <Chip
            label="All"
            size="small"
            onClick={() => setExamPreviewNavSectionTab('all')}
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              cursor: 'pointer',
              bgcolor: examPreviewNavSectionTab === 'all' ? '#236B59' : '#F3F4F6',
              color: examPreviewNavSectionTab === 'all' ? '#FFFFFF' : '#374151',
            }}
          />
          {(examPreviewQuizData || []).map((sec, sIdx) => {
            const sName = (sec.subject_name || sec.name || `S${sIdx + 1}`).slice(0, 8);
            const isSel = examPreviewNavSectionTab === sIdx;
            return (
              <Chip
                key={sIdx}
                label={sName}
                size="small"
                onClick={() => setExamPreviewNavSectionTab(sIdx)}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  bgcolor: isSel ? '#236B59' : '#F3F4F6',
                  color: isSel ? '#FFFFFF' : '#374151',
                }}
              />
            );
          })}
        </Box>

        {(examPreviewQuizData || []).map((sec, sIdx) => {
          if (examPreviewNavSectionTab !== 'all' && examPreviewNavSectionTab !== sIdx) return null;
          const secQuestions = sec.questions || [];
          const secStartNo = examPreviewQuizData.slice(0, sIdx).reduce((acc, s) => acc + (s.questions?.length || 0), 0);

          return (
            <Box key={sIdx} sx={{ mb: 2 }}>
              {examPreviewQuizData.length > 1 && examPreviewNavSectionTab === 'all' && (
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6B7280', display: 'block', mb: 0.75 }}>
                  {sec.subject_name || sec.name || `Section ${sIdx + 1}`}
                </Typography>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.8 }}>
                {secQuestions.map((_, qIdx) => {
                  const qNo = secStartNo + qIdx + 1;
                  const qDetail =
                            allQuestionsFlattened.find((q) => String(q.question_id || q.questionId) === String(sec?.questions?.[qIdx]?.questionId || sec?.questions?.[qIdx]?.id)) ||
                    allQuestionsFlattened.find((q) => String(q.qNo) === String(qNo)) ||
                    allQuestionsFlattened[qNo - 1];

                  const studentAns = qDetail?.sa;
                  const isCorrect = qDetail?.isCorrect;
                  const hasAttempt =
                    studentAns !== undefined &&
                    studentAns !== null &&
                    studentAns !== '' &&
                    studentAns !== '-' &&
                    studentAns !== 'Not Attempted';

                  let bg = '#F9FAFB';
                  let fg = '#6B7280';
                  let border = '1px solid #E5E7EB';

                  if (hasAttempt) {
                    if (isCorrect) {
                      bg = '#2E7D32';
                      fg = '#FFFFFF';
                      border = '1px solid #2E7D32';
                    } else {
                      bg = '#D32F2F';
                      fg = '#FFFFFF';
                      border = '1px solid #D32F2F';
                    }
                  }

                  return (
                    <Box
                      key={qIdx}
                      onClick={() => handleJumpToExamPreviewQuestion(sIdx, qIdx)}
                      sx={{
                        width: '100%',
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        bgcolor: bg,
                        color: fg,
                        border: border,
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        cursor: 'pointer',
                      }}
                    >
                      {qNo}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Drawer>
    </Container>
  );
}

ExaminationProfileView.propTypes = {
  id: PropTypes.string,
  propId: PropTypes.string,
  quizId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isEmbedded: PropTypes.bool,
  quizzes: PropTypes.array,
  isTalentHunt: PropTypes.bool,
};
